import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import { MenuIcon, SearchIcon, AlertIcon, UserIcon, SettingsIcon } from '../icons/NavigationIcons';
import { ThemeToggleButton } from '../ThemeToggleButton';

export const AppHeader: React.FC = () => {
  const { isExpanded, toggleMobileSidebar } = useSidebar();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Mock notifications
  const notifications = [
    { id: 1, title: 'Budget Alert', message: 'Campaign X spent 80% of budget', time: '5m ago', unread: true },
    { id: 2, title: 'Performance Alert', message: 'ROAS dropped below 2.0', time: '1h ago', unread: true },
    { id: 3, title: 'System Update', message: 'New features available', time: '2h ago', unread: false },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  // Generate user initials from full name
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  return (
    <header
      className={`
        fixed top-0 right-0 z-30 h-16
        ${isExpanded ? 'lg:left-72' : 'lg:left-20'}
        left-0 transition-all duration-300
        bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800
        flex items-center justify-between px-4 lg:px-6
      `}
    >
      {/* Left Side - Mobile Menu + Search */}
      <div className="flex items-center space-x-4 flex-1">
        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleMobileSidebar}
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg
                   hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Open menu"
        >
          <MenuIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
        </button>

        {/* Search Bar */}
        <div className="hidden md:flex items-center flex-1 max-w-md">
          <div className="relative w-full">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns, ads..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700
                       rounded-lg bg-gray-50 dark:bg-gray-800
                       text-gray-900 dark:text-gray-100
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:outline-none focus:ring-2 focus:ring-gp-sungold focus:border-transparent
                       transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Right Side - Theme Toggle + Notifications + User Menu */}
      <div className="flex items-center space-x-2">
        {/* Theme Toggle */}
        <ThemeToggleButton />

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
            className="relative flex items-center justify-center w-10 h-10 rounded-lg
                     hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Notifications"
          >
            <AlertIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-gp-orange rounded-full" />
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowNotifications(false)}
              />
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg
                            border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-1 text-xs font-medium bg-gp-orange text-white rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`
                        p-4 border-b border-gray-200 dark:border-gray-700
                        hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer
                        ${notification.unread ? 'bg-gp-sungold-50 dark:bg-gp-sungold-900/20' : ''}
                      `}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`
                          w-2 h-2 mt-2 rounded-full flex-shrink-0
                          ${notification.unread ? 'bg-gp-sungold' : 'bg-gray-300'}
                        `} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                  <button className="w-full text-center text-sm text-gp-sungold hover:text-gp-sungold-600 font-medium">
                    View all notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg
                     hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="User menu"
          >
            <div className="w-8 h-8 rounded-full bg-gp-sungold flex items-center justify-center">
              <span className="text-white font-semibold text-sm">{getInitials(user?.full_name)}</span>
            </div>
            <span className="hidden lg:block text-sm font-medium text-gray-700 dark:text-gray-300">
              {user?.full_name || 'User'}
            </span>
          </button>

          {/* User Menu Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg
                            border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user?.email}</p>
                </div>
                <div className="py-2">
                  <Link
                    to="/settings/profile"
                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300
                             hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <UserIcon className="w-5 h-5" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/integrations"
                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300
                             hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <SettingsIcon className="w-5 h-5" />
                    <span>Settings</span>
                  </Link>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gp-orange
                             hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
